import React from 'react';
import style from './buttonLaunchingPopover.module.scss';
import CustomReactPopover, { Align, Position } from './popover/custom-react-popover';

interface ButtonLaunchingPopoverProps {
    buttonClassName?: string;
    popoverBodyClassName?: string;
    popoverPosition?: Position;
    popoverHorizontalAlignment?: Align;
    childComponentFactory?: (closePopover: () => void) => React.ReactNode;
    closeOnClickOutside?: boolean;
    focusOnShow?: boolean;
}
interface ButtonLaunchingPopoverState {
    isOpen: boolean;
}

class ButtonLaunchingPopover extends React.Component<ButtonLaunchingPopoverProps,
    ButtonLaunchingPopoverState> {
    private button: HTMLButtonElement | null;

    constructor(props: ButtonLaunchingPopoverProps) {
        super(props);
        this.state = { isOpen: false };
    }

    private togglePopover = () => this.setState({ isOpen: !this.state.isOpen });
    private closePopover = () => this.setState({ isOpen: false });
    openPopover = () => this.setState({ isOpen: true });

    componentDidUpdate(prevProps: Readonly<ButtonLaunchingPopoverProps>, prevState: Readonly<ButtonLaunchingPopoverState>): void {
        if (prevState.isOpen && !this.state.isOpen && !!this.button) {
            this.button.focus(); // resume tab position at the button that launched the popover
        }
    }

    render(): JSX.Element {
        return (
            <CustomReactPopover
                isOpen={this.state.isOpen}
                position={this.props.popoverPosition || 'bottom'}
                align={this.props.popoverHorizontalAlignment || 'center'}
                transitionDuration={0.15} // seconds
                containerStyle={{overflow: 'visible'}}
                padding={15}
                windowBorderPadding={15}
                onClickOutside={this.props.closeOnClickOutside ? this.closePopover : undefined}
                content={({nudge}) => (
                    (this.state.isOpen
                            ? <PopoverContentContainer
                                nudgeLeft={nudge.left}
                                popoverBodyClassName={this.props.popoverBodyClassName}
                                focusOnMount={this.props.focusOnShow}
                            >
                                {this.props.childComponentFactory
                                    ? this.props.childComponentFactory(this.closePopover)
                                    : this.props.children}
                            </PopoverContentContainer>
                            : <div/>
                    )
                )}
            >
                <button
                    type="button" // disables the default form-submitting behaviour of the button
                    className={this.props.buttonClassName}
                    onClick={this.togglePopover}
                    ref={r => this.button = r}
                >
                    {this.props.children}
                </button>
            </CustomReactPopover>
        );
    }
}

interface PopoverContentContainerProps {
    nudgeLeft: number;
    popoverBodyClassName?: string;
    focusOnMount?: boolean;
}

// Use a distinct component for the 'open' state of the popover so that it, and its children, are correctly mounted and unmounted
class PopoverContentContainer extends React.Component<PopoverContentContainerProps> {
    private container: HTMLDivElement | null;

    componentDidMount(): void {
        if (this.props.focusOnMount) {
            window.setTimeout(() => this.scrollIntoViewAndSetFocus(), 10);
        }
    }

    scrollIntoViewAndSetFocus(): void {
        if (!!this.container) {
            (this.container.querySelector('input, textarea, button, [tabindex="0"]') as HTMLElement || this.container).focus();
            this.container.scrollIntoView({block: 'nearest'});
        }
    }

    render(): JSX.Element {
        return (
            <div
                style={{ left: this.props.nudgeLeft }}
                className={style.PopoverBodyContainer}
                ref={r => this.container = r}
            >
                <div className={this.props.popoverBodyClassName}>
                    {this.props.children}
                </div>
                <div className={style.PopoverFooterSpacing} />
            </div>
        );
    }
}

export { ButtonLaunchingPopover };
